import type { CrumbRoute } from 'constants/EntityTitles';
import { TITLE_ENTITY } from 'constants/EntityTitles';
import { ENTITY_TYPE } from 'modules/app/constants';

/* ------------------------------------------------------------------ *
 * Management bases
 * ------------------------------------------------------------------ */
const DEALER_MANAGEMENT_PATH = { path: '/dealer/management', handle: { crumb: TITLE_ENTITY.DEALER_MANAGEMENT } } satisfies CrumbRoute;
const GROUP_MANAGEMENT_PATH = { path: '/group/management', handle: { crumb: TITLE_ENTITY.GROUP_MANAGEMENT } } satisfies CrumbRoute;
const PERSONAL_MANAGEMENT_PATH = { path: '/personal/management' };
const ADMINISTRATION_PATH = { path: '/administration' };

/* ------------------------------------------------------------------ *
 * GENERIC
 * ------------------------------------------------------------------ */
const HOME_PATH = { path: '/' };
const ERROR_404_PATH = { path: '/404' };
const ERROR_GLOBAL_PATH = { path: '/error' };
const MAGIC_LINK_PATH = {
  path: '/magicLink',
  searchParams: { otp: 'otp', username: 'username', expireOn: 'expireOn' } as const,
};
const FORGOT_PASSWORD_PATH = { path: '/forgotpassword' };
const PROFILE_PATH = { path: '/profile' };
const PAYMENT_PATH = { path: '/payment' };

/* ------------------------------------------------------------------ *
 * AFTERSALES
 * ------------------------------------------------------------------ */
const AFTERSALES_PATH = { path: '/aftersales' };
const AFTERSALES_DETAILED_PATH = { path: `${AFTERSALES_PATH.path}/:interventionId` };

const BODYWORKS_PATH = { path: '/bodyworks' };
const BODYWORKS_DETAILED_PATH = { path: `${BODYWORKS_PATH.path}/:interventionId` };

const REMINDERS_PATH = { path: '/reminders' };
const REMINDERS_DETAILED_PATH = { path: `${REMINDERS_PATH.path}/:interventionId` };

/* ------------------------------------------------------------------ *
 * SELF-SERVICE (interventions)
 * ------------------------------------------------------------------ */
const SELF_SERVICE_KANBAN_PATH = { path: '/self-services/kanban' };
const SELF_SERVICE_MOBILITY_PATH = { path: '/self-services/mobility' };
const FOLLOW_UP_PATH = { path: '/follow-up/:mode' };

/* ------------------------------------------------------------------ *
 * PARTS
 * ------------------------------------------------------------------ */
const PARTS_PATH = { path: '/parts' };
const PARTS_DETAILED_PATH = { path: `${PARTS_PATH.path}/:interventionId` };

const PARTS_REMINDER_PATH = { path: '/parts-reminders' };
const PARTS_REMINDER_DETAILED_PATH = { path: `${PARTS_REMINDER_PATH.path}/:interventionId` };

/* ------------------------------------------------------------------ *
 * SALES
 * ------------------------------------------------------------------ */
const LEADS_PATH = { path: '/leads' };

const SALES_PATH = { path: '/sales' };
const SALES_DETAILED_PATH = { path: `${SALES_PATH.path}/:interventionId` };

/* ------------------------------------------------------------------ *
 * AUDIT
 * ------------------------------------------------------------------ */
const AUDITS_PATH = { path: '/audits' };
const AUDITS_DETAILED_PATH = { path: `${AUDITS_PATH.path}/:auditId`, };

/* ------------------------------------------------------------------ *
 * STATISTICS (per-dealer)
 * ------------------------------------------------------------------ */
const STATS_ODITOO_PATH = {
  path: '/statistics/oditoo/:dealerId',
  simplePath: '/statistics/oditoo',
};

const STATS_AFTER_SALES_PATH = { path: '/statistics/after-sales' };
const STATS_AFTER_SALES_DETAILS_PATH = { path: `${STATS_AFTER_SALES_PATH.path}/:dealerId` };

const STATS_PARTS_PATH = { path: '/statistics/parts' };
const STATS_PARTS_DETAILS_PATH = { path: `${STATS_PARTS_PATH.path}/:dealerId` };

const STATS_SALES_PATH = { path: '/statistics/sales' };
const STATS_SALES_DETAILS_PATH = { path: `${STATS_SALES_PATH.path}/:dealerId` };

const SELF_SERVICE_STATS_DASHBOARD_PATH = { path: '/statistics/self-service' };
const SELF_SERVICE_STATS_DETAILS_PATH = { path: `${SELF_SERVICE_STATS_DASHBOARD_PATH.path}/:dealerId` };

/* ------------------------------------------------------------------ *
 * STATISTICS (manufacturer / brand)
 * ------------------------------------------------------------------ */
const MANUFACTURER_STATS_PARTS_PATH = { path: '/brand_statistics/parts' };
const MANUFACTURER_STATS_PARTS_DETAILS_PATH = { path: `${MANUFACTURER_STATS_PARTS_PATH.path}/:dealerId` };

const MANUFACTURER_STATS_SALES_PATH = { path: '/brand_statistics/sales' };
const MANUFACTURER_STATS_SALES_DETAILS_PATH = { path: `${MANUFACTURER_STATS_SALES_PATH.path}/:dealerId` };

const MANUFACTURER_STATS_AFTER_SALES_PATH = { path: '/brand_statistics/after-sales' };
const MANUFACTURER_STATS_AFTER_SALES_DETAILS_PATH = { path: `${MANUFACTURER_STATS_AFTER_SALES_PATH.path}/:dealerId` };

const MANUFACTURER_STATS_SELF_SERVICE_PATH = { path: '/brand_statistics/self-service' };
const MANUFACTURER_STATS_SELF_SERVICE_DETAILS_PATH = { path: `${MANUFACTURER_STATS_SELF_SERVICE_PATH.path}/:dealerId` };

/* ------------------------------------------------------------------ *
 * CAMPAIGNS / CONTACT LISTS / RELEASES / CRM
 * ------------------------------------------------------------------ */
const CAMPAIGNS_DASHBOARD_PATH = { path: '/campaigns' };
const CAMPAIGNS_DETAILS_PATH = { path: `${CAMPAIGNS_DASHBOARD_PATH.path}/:campaignId` };

const CONTACT_LISTS_DASHBOARD_PATH = { path: '/contactLists' };
const CONTACT_LISTS_DETAILS_PATH = { path: `${CONTACT_LISTS_DASHBOARD_PATH.path}/:contactListId` };

const RELEASES_DASHBOARD_PATH = { path: '/releases' };
const RELEASES_DETAILS_PATH = { path: `${RELEASES_DASHBOARD_PATH.path}/:releaseId` };

const CUSTOMERS_PATH = { path: '/customers' };
const CUSTOMERS_DETAILED_PATH = { path: `${CUSTOMERS_PATH.path}/:customerId` };

/* ------------------------------------------------------------------ *
 * PAYMENTS / BILLING
 * ------------------------------------------------------------------ */
const BILLING_PATH = { path: '/billing/:entity' };
const MESSAGE_BILLING_PATH = { path: '/billing/message' };
const CLIPPING_BILLING_PATH = { path: '/billing/clipping' };
const AUTOMATED_DOCUMENT_BILLING_PATH = { path: '/billing/documents' };
// The segment is lower-cased `BILLING_TYPE.PHONE_AGENT`: `getEntity` upper-cases it before
// matching, so a hyphen would silently resolve to the Message screen instead.
const PHONE_AGENT_BILLING_PATH = { path: '/billing/phone_agent' };

/* ------------------------------------------------------------------ *
 * CONFIGURATOR
 * ------------------------------------------------------------------ */

const CONFIGURATOR_OFFERS_PATH = { path: '/configurator/offers' };
const CONFIGURATOR_OFFERS_DETAILED_PATH = { path: `${CONFIGURATOR_OFFERS_PATH.path}/:offerId` };

/* ------------------------------------------------------------------ *
 * KEYDEPOSIT (lockers / appointments)
 * ------------------------------------------------------------------ */
const KEY_DROP_OFF_LOCKER_PLANNING_PATH = { path: '/lockers' };
const KEY_DROP_OFF_LOCKER_DETAILED_PATH = { path: `${KEY_DROP_OFF_LOCKER_PLANNING_PATH.path}/:lockerId` };

const KEY_DROP_OFF_APPOINTMENTS_PATH = { path: '/appointments' };
const KEY_DROP_OFF_APPOINTMENTS_DETAILED_PATH = { path: `${KEY_DROP_OFF_APPOINTMENTS_PATH.path}/:appointmentId` };

/* ------------------------------------------------------------------ *
 * CHAT / NOTIFICATIONS
 * ------------------------------------------------------------------ */
const CHAT_DASHBOARD_PATH = {
  path: '/conversations/:conversationId',
  simplePath: '/conversations',
};
const USER_NOTIFICATIONS_PATH = { path: '/notifications' };
const USER_NOTIFICATIONS_SETTINGS_PATH = { path: '/personal/config/notifications' };

/* ------------------------------------------------------------------ *
 * DEALER ADMINISTRATION / CONFIGURATION PAGES
 * ------------------------------------------------------------------ */
const DEALERS_DETAILS_PATH = { path: `${ADMINISTRATION_PATH.path}/dealers/:dealerId` };
const DEALER_CONFIG_PAGE_PATH = { path: '/dealer/config/:dealerId/:tabKey?' };
const GROUP_CONFIG_PAGE_PATH = { path: '/group/config/:groupId/:tabKey?' };

/* ================================================================== *
 * DEALER MANAGEMENT
 * ================================================================== */
const DEALER_USER_GROUPS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/user-groups`, handle: { crumb: TITLE_ENTITY.USER_GROUPS } } satisfies CrumbRoute;
const DEALER_DEVICES_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/devices`, handle: { crumb: TITLE_ENTITY.DEVICES } } satisfies CrumbRoute;
const DEALER_WHITELIST_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/whitelist`, handle: { crumb: TITLE_ENTITY.WHITELIST } } satisfies CrumbRoute;
const DEALER_HARDWARES_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/hardwares`, handle: { crumb: TITLE_ENTITY.HARDWARES } } satisfies CrumbRoute;
const DEALER_SAFES_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/safes`, handle: { crumb: TITLE_ENTITY.SAFES } } satisfies CrumbRoute;
const DEALER_MEDIA_LIBRARY_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/media-library`, handle: { crumb: TITLE_ENTITY.MEDIA_LIBRARY } } satisfies CrumbRoute;
const DEALER_PUBLIC_TOKENS_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/publicTokens`, handle: { crumb: TITLE_ENTITY.PUBLIC_TOKENS } } satisfies CrumbRoute;
const DEALER_SMS_ACCOUNTS_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/sms-accounts`, handle: { crumb: TITLE_ENTITY.SMS_ACCOUNTS } } satisfies CrumbRoute;

const DEALER_USERS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/users`, handle: { crumb: TITLE_ENTITY.USERS } } satisfies CrumbRoute;
const DEALER_USERS_DETAILED_PATH = { path: `${DEALER_USERS_PATH.path}/:userId`, handle: { crumb: TITLE_ENTITY.USER, entityCrumb: true, entityIdParam: 'userId' } } satisfies CrumbRoute;

const DEALER_PAYERS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/payers`, handle: { crumb: TITLE_ENTITY.PAYERS } } satisfies CrumbRoute;
const DEALER_PAYERS_DETAILS_PATH = { path: `${DEALER_PAYERS_PATH.path}/:payerId`, handle: { crumb: TITLE_ENTITY.PAYER, entityCrumb: true, entityIdParam: 'payerId' } } satisfies CrumbRoute;

const DEALER_KIOSKS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/kiosks`, handle: { crumb: TITLE_ENTITY.KIOSKS } } satisfies CrumbRoute;
const DEALER_KIOSKS_DETAILS_PATH = { path: `${DEALER_KIOSKS_PATH.path}/:kioskId`, handle: { crumb: TITLE_ENTITY.KIOSK, entityCrumb: true, entityIdParam: 'kioskId' } } satisfies CrumbRoute;

const DEALER_CAMERAS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/cameras`, handle: { crumb: TITLE_ENTITY.CAMERAS } } satisfies CrumbRoute;
const DEALER_CAMERAS_DETAILS_PATH = { path: `${DEALER_CAMERAS_PATH.path}/:cameraId`, handle: { crumb: TITLE_ENTITY.CAMERA, entityCrumb: true, entityIdParam: 'cameraId' } } satisfies CrumbRoute;

const DEALER_AUDIT_MODELS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/audit-models`, handle: { crumb: TITLE_ENTITY.AUDIT_MODELS } } satisfies CrumbRoute;
const DEALER_AUDIT_MODELS_DETAILED_PATH = { path: `${DEALER_AUDIT_MODELS_PATH.path}/:auditModelId`, handle: { crumb: TITLE_ENTITY.AUDIT_MODEL, entityCrumb: true, entityIdParam: 'auditModelId' }, } satisfies CrumbRoute;

const DEALER_CHECKLISTS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/checklists`, handle: { crumb: TITLE_ENTITY.CHECKLISTS } } satisfies CrumbRoute;
const DEALER_CHECKLISTS_DETAILED_PATH = { path: `${DEALER_CHECKLISTS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.CHECKLIST, entityCrumb: true, entityIdParam: 'checklistId' } } satisfies CrumbRoute;

const DEALER_GUIDELINES_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/guidelines`, handle: { crumb: TITLE_ENTITY.GUIDELINES } } satisfies CrumbRoute;
const DEALER_GUIDELINE_DETAILED_PATH = { path: `${DEALER_GUIDELINES_PATH.path}/:guidelineId`, handle: { crumb: TITLE_ENTITY.GUIDELINE, entityCrumb: true, entityIdParam: 'guidelineId' } } satisfies CrumbRoute;

const DEALER_PREDEFINED_MESSAGES_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/predefined-messages`, handle: { crumb: TITLE_ENTITY.PREDEFINED_MESSAGES } } satisfies CrumbRoute;
const DEALER_PREDEFINED_MESSAGES_DETAILED_PATH = { path: `${DEALER_PREDEFINED_MESSAGES_PATH.path}/:predefinedMessageId`, handle: { crumb: TITLE_ENTITY.PREDEFINED_MESSAGE, entityCrumb: true, entityIdParam: 'predefinedMessageId' } } satisfies CrumbRoute;

const DEALER_OFFERS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/sales-offers`, handle: { crumb: TITLE_ENTITY.OFFERS } } satisfies CrumbRoute;
const DEALER_OFFERS_DETAILED_PATH = { path: `${DEALER_OFFERS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.OFFER, entityCrumb: true, entityIdParam: 'checklistId' } } satisfies CrumbRoute;

const DEALER_PARTS_OFFERS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/parts-offers`, handle: { crumb: TITLE_ENTITY.PARTS_OFFERS } } satisfies CrumbRoute;
const DEALER_PARTS_OFFERS_DETAILS_PATH = { path: `${DEALER_PARTS_OFFERS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.PARTS_OFFER, entityCrumb: true, entityIdParam: 'checklistId' } } satisfies CrumbRoute;

const DEALER_STEPS_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/steps/:mode`, handle: { crumb: TITLE_ENTITY.STEPS } } satisfies CrumbRoute;
const DEALER_STEPS_DETAILS_PATH = { path: `${DEALER_STEPS_DASHBOARD_PATH.path}/:stepId`, handle: { crumb: TITLE_ENTITY.STEP, entityCrumb: true, entityIdParam: 'stepId' } } satisfies CrumbRoute;

const DEALER_TAGS_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/tags/:mode`, handle: { crumb: TITLE_ENTITY.TAGS } } satisfies CrumbRoute;
const DEALER_TAGS_DETAILS_PATH = { path: `${DEALER_TAGS_DASHBOARD_PATH.path}/:tagId`, handle: { crumb: TITLE_ENTITY.TAG, entityCrumb: true, entityIdParam: 'tagId' } } satisfies CrumbRoute;

const DEALER_GATE_MANAGEMENT_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/gates`, handle: { crumb: TITLE_ENTITY.GATES } } satisfies CrumbRoute;
const DEALER_GATE_MANAGEMENT_DETAILS_PATH = { path: `${DEALER_GATE_MANAGEMENT_DASHBOARD_PATH.path}/:gateId`, handle: { crumb: TITLE_ENTITY.GATE, entityCrumb: true, entityIdParam: 'gateId' } } satisfies CrumbRoute;

const DEALER_CONFIG_SCREENSAVERS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/screensavers`, handle: { crumb: TITLE_ENTITY.SCREENSAVERS } } satisfies CrumbRoute;
const DEALER_CONFIG_SCREENSAVER_DETAILS_PATH = { path: `${DEALER_CONFIG_SCREENSAVERS_PATH.path}/:screensaverId`, handle: { crumb: TITLE_ENTITY.SCREENSAVER, entityCrumb: true, entityIdParam: 'screensaverId' } } satisfies CrumbRoute;

const DEALER_EXTRA_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/extras`, handle: { crumb: TITLE_ENTITY.EXTRAS } } satisfies CrumbRoute;
const DEALER_EXTRA_DETAILED_PATH = { path: `${DEALER_EXTRA_DASHBOARD_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.EXTRA, entityCrumb: true, entityIdParam: 'checklistId' } } satisfies CrumbRoute;

const DEALER_QUESTION_MODEL_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/questionModels`, handle: { crumb: TITLE_ENTITY.QUESTION_MODELS }, } satisfies CrumbRoute;
const DEALER_QUESTION_MODEL_DETAILED_PATH = { path: `${DEALER_QUESTION_MODEL_DASHBOARD_PATH.path}/:questionModelId`, handle: { crumb: TITLE_ENTITY.QUESTION_MODEL, entityCrumb: true, entityIdParam: 'questionModelId' }, } satisfies CrumbRoute;

const DEALER_VEHICLE_CHECK_MODEL_DASHBOARD_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/vehicleCheckModel`, handle: { crumb: TITLE_ENTITY.VEHICLE_CHECK_MODELS }, } satisfies CrumbRoute;
const DEALER_VEHICLE_CHECK_MODEL_DETAILED_PATH = { path: `${DEALER_VEHICLE_CHECK_MODEL_DASHBOARD_PATH.path}/:vehicleCheckModelId`, handle: { crumb: TITLE_ENTITY.VEHICLE_CHECK_MODEL, entityCrumb: true, entityIdParam: 'vehicleCheckModelId' } } satisfies CrumbRoute;

const CONFIGURATOR_MODELS_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/models`, handle: { crumb: TITLE_ENTITY.MODELS } } satisfies CrumbRoute;
const DEALER_CCM_PATH = { path: `${DEALER_MANAGEMENT_PATH.path}/ccm/:tabKey?`, handle: { crumb: TITLE_ENTITY.CCM }, } satisfies CrumbRoute;

/* ================================================================== *
 * GROUP MANAGEMENT
 * ================================================================== */
const GROUP_CHECKLISTS_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/checklists`, handle: { crumb: TITLE_ENTITY.CHECKLISTS }, } satisfies CrumbRoute;
const GROUP_CCM_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/ccm/:tabKey?`, handle: { crumb: TITLE_ENTITY.CCM }, } satisfies CrumbRoute;
const GROUP_TEMPLATES_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/templates`, handle: { crumb: TITLE_ENTITY.TEMPLATES }, } satisfies CrumbRoute;
const GROUP_GUIDELINES_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/guidelines`, handle: { crumb: TITLE_ENTITY.GUIDELINES }, } satisfies CrumbRoute;
const GROUP_GUIDELINE_DETAILED_PATH = { path: `${GROUP_GUIDELINES_PATH.path}/:guidelineId`, handle: { crumb: TITLE_ENTITY.GUIDELINE, entityCrumb: true, entityIdParam: 'guidelineId' }, } satisfies CrumbRoute;
const GROUP_OFFERS_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/sales-offers`, handle: { crumb: TITLE_ENTITY.OFFERS }, } satisfies CrumbRoute;
const GROUP_MEDIA_LIBRARY_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/media-library`, handle: { crumb: TITLE_ENTITY.MEDIA_LIBRARY }, } satisfies CrumbRoute;
const GROUP_DEVICES_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/devices`, handle: { crumb: TITLE_ENTITY.DEVICES }, } satisfies CrumbRoute;
const GROUP_PARTS_OFFERS_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/parts-offers`, handle: { crumb: TITLE_ENTITY.PARTS_OFFERS }, } satisfies CrumbRoute;
const GROUP_PUBLIC_TOKENS_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/publicTokens`, handle: { crumb: TITLE_ENTITY.PUBLIC_TOKENS }, } satisfies CrumbRoute;
const GROUP_SMS_ACCOUNTS_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/sms-accounts`, handle: { crumb: TITLE_ENTITY.SMS_ACCOUNTS }, } satisfies CrumbRoute;

const GROUP_CHECKLISTS_DETAILED_PATH = { path: `${GROUP_CHECKLISTS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.CHECKLIST, entityCrumb: true, entityIdParam: 'checklistId' }, } satisfies CrumbRoute;
const GROUP_OFFERS_DETAILED_PATH = { path: `${GROUP_OFFERS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.OFFER, entityCrumb: true, entityIdParam: 'checklistId' }, } satisfies CrumbRoute;
const GROUP_PARTS_OFFERS_DETAILS_PATH = { path: `${GROUP_PARTS_OFFERS_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.PARTS_OFFER, entityCrumb: true, entityIdParam: 'checklistId' }, } satisfies CrumbRoute;

const GROUP_EXTRA_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/extras`, handle: { crumb: TITLE_ENTITY.EXTRAS }, } satisfies CrumbRoute;
const GROUP_EXTRA_DETAILED_PATH = { path: `${GROUP_EXTRA_DASHBOARD_PATH.path}/:checklistId`, handle: { crumb: TITLE_ENTITY.EXTRA, entityCrumb: true, entityIdParam: 'checklistId' }, } satisfies CrumbRoute;

const GROUP_QUESTION_MODEL_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/questionModels`, handle: { crumb: TITLE_ENTITY.QUESTION_MODELS }, } satisfies CrumbRoute;
const GROUP_QUESTION_MODEL_DETAILED_PATH = { path: `${GROUP_QUESTION_MODEL_DASHBOARD_PATH.path}/:questionModelId`, handle: { crumb: TITLE_ENTITY.QUESTION_MODEL, entityCrumb: true, entityIdParam: 'questionModelId' }, } satisfies CrumbRoute;

const GROUP_VEHICLE_CHECK_MODEL_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/vehicleCheckModel`, handle: { crumb: TITLE_ENTITY.VEHICLE_CHECK_MODELS }, } satisfies CrumbRoute;
const GROUP_VEHICLE_CHECK_MODEL_DETAILED_PATH = { path: `${GROUP_VEHICLE_CHECK_MODEL_DASHBOARD_PATH.path}/:vehicleCheckModelId`, handle: { crumb: TITLE_ENTITY.VEHICLE_CHECK_MODEL, entityCrumb: true, entityIdParam: 'vehicleCheckModelId' }, } satisfies CrumbRoute;

const GROUP_PAYERS_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/payers`, handle: { crumb: TITLE_ENTITY.PAYERS }, } satisfies CrumbRoute;
const GROUP_PAYERS_DETAILS_PATH = { path: `${GROUP_PAYERS_PATH.path}/:payerId`, handle: { crumb: TITLE_ENTITY.PAYER, entityCrumb: true, entityIdParam: 'payerId' }, } satisfies CrumbRoute;

const GROUP_TAGS_DASHBOARD_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/tags/:mode`, handle: { crumb: TITLE_ENTITY.TAGS }, } satisfies CrumbRoute;
const GROUP_TAGS_DETAILS_PATH = { path: `${GROUP_TAGS_DASHBOARD_PATH.path}/:tagId`, handle: { crumb: TITLE_ENTITY.TAG, entityCrumb: true, entityIdParam: 'tagId' }, } satisfies CrumbRoute;

const GROUP_CONFIG_SCREENSAVERS_PATH = { path: `${GROUP_MANAGEMENT_PATH.path}/screensavers`, handle: { crumb: TITLE_ENTITY.SCREENSAVERS }, } satisfies CrumbRoute;
const GROUP_CONFIG_SCREENSAVER_DETAILS_PATH = { path: `${GROUP_CONFIG_SCREENSAVERS_PATH.path}/:screensaverId`, handle: { crumb: TITLE_ENTITY.SCREENSAVER, entityCrumb: true, entityIdParam: 'screensaverId' }, } satisfies CrumbRoute;

/* ================================================================== *
 * PERSONAL MANAGEMENT
 * ================================================================== */
const PERSONAL_MEDIA_LIBRARY_PATH = { path: `${PERSONAL_MANAGEMENT_PATH.path}/media-library` };

/**
 * @typedef {{
 *    path: string;
 *    strict: boolean;
 *    exact: boolean;
 *    simplePath?: string;
 *    redirectPath?: string;
 *    [field:string]: any;
 * }} FBPath
 */

/**
 * @typedef {{[field:string] : FBPath }} FBPathCollection
 */

/** @type {FBPathCollection} */
export const PATH = {
  /* --- GENERIC --- */
  HOME: HOME_PATH,
  ERROR_404: ERROR_404_PATH,
  ERROR_GLOBAL: ERROR_GLOBAL_PATH,
  MAGIC_LINK: MAGIC_LINK_PATH,
  FORGOT_PASSWORD: FORGOT_PASSWORD_PATH,
  PROFILE: PROFILE_PATH,
  PAYMENT: PAYMENT_PATH,

  /* --- AFTERSALES --- */
  AFTERSALES: AFTERSALES_PATH,
  AFTERSALES_DETAILED: AFTERSALES_DETAILED_PATH,

  BODYWORKS: BODYWORKS_PATH,
  BODYWORKS_DETAILED: BODYWORKS_DETAILED_PATH,

  REMINDERS: REMINDERS_PATH,
  REMINDERS_DETAILED: REMINDERS_DETAILED_PATH,

  /* --- SELF-SERVICE (interventions) --- */
  SELF_SERVICE_KANBAN: SELF_SERVICE_KANBAN_PATH,
  SELF_SERVICE_MOBILITY: SELF_SERVICE_MOBILITY_PATH,
  FOLLOW_UP: FOLLOW_UP_PATH,

  /* --- PARTS --- */
  PARTS: PARTS_PATH,
  PARTS_DETAILED: PARTS_DETAILED_PATH,

  PARTS_REMINDER: PARTS_REMINDER_PATH,
  PARTS_REMINDER_DETAILED: PARTS_REMINDER_DETAILED_PATH,

  /* --- SALES --- */
  LEADS: LEADS_PATH,

  SALES: SALES_PATH,
  SALES_DETAILED: SALES_DETAILED_PATH,

  /* --- AUDIT --- */
  AUDITS: AUDITS_PATH,
  AUDITS_DETAILED: AUDITS_DETAILED_PATH,

  /* --- STATISTICS (per-dealer) --- */
  STATS_ODITOO: STATS_ODITOO_PATH,

  STATS_AFTER_SALES: STATS_AFTER_SALES_PATH,
  STATS_AFTER_SALES_DETAILS: STATS_AFTER_SALES_DETAILS_PATH,

  STATS_PARTS: STATS_PARTS_PATH,
  STATS_PARTS_DETAILS: STATS_PARTS_DETAILS_PATH,

  STATS_SALES: STATS_SALES_PATH,
  STATS_SALES_DETAILS: STATS_SALES_DETAILS_PATH,

  SELF_SERVICE_STATS_DASHBOARD: SELF_SERVICE_STATS_DASHBOARD_PATH,
  SELF_SERVICE_STATS_DETAILS: SELF_SERVICE_STATS_DETAILS_PATH,

  /* --- STATISTICS (manufacturer / brand) --- */
  MANUFACTURER_STATS_PARTS: MANUFACTURER_STATS_PARTS_PATH,
  MANUFACTURER_STATS_PARTS_DETAILS: MANUFACTURER_STATS_PARTS_DETAILS_PATH,

  MANUFACTURER_STATS_SALES: MANUFACTURER_STATS_SALES_PATH,
  MANUFACTURER_STATS_SALES_DETAILS: MANUFACTURER_STATS_SALES_DETAILS_PATH,

  MANUFACTURER_STATS_AFTER_SALES: MANUFACTURER_STATS_AFTER_SALES_PATH,
  MANUFACTURER_STATS_AFTER_SALES_DETAILS: MANUFACTURER_STATS_AFTER_SALES_DETAILS_PATH,

  MANUFACTURER_STATS_SELF_SERVICE: MANUFACTURER_STATS_SELF_SERVICE_PATH,
  MANUFACTURER_STATS_SELF_SERVICE_DETAILS: MANUFACTURER_STATS_SELF_SERVICE_DETAILS_PATH,

  /* --- CAMPAIGNS / CONTACT LISTS / RELEASES / CRM --- */
  CAMPAIGNS_DASHBOARD: CAMPAIGNS_DASHBOARD_PATH,
  CAMPAIGNS_DETAILS: CAMPAIGNS_DETAILS_PATH,

  CONTACT_LISTS_DASHBOARD: CONTACT_LISTS_DASHBOARD_PATH,
  CONTACT_LISTS_DETAILS: CONTACT_LISTS_DETAILS_PATH,

  RELEASES_DASHBOARD: RELEASES_DASHBOARD_PATH,
  RELEASES_DETAILS: RELEASES_DETAILS_PATH,

  CUSTOMERS: CUSTOMERS_PATH,
  CUSTOMERS_DETAILED: CUSTOMERS_DETAILED_PATH,

  /* --- PAYMENTS / BILLING --- */
  BILLING: BILLING_PATH,
  MESSAGE_BILLING: MESSAGE_BILLING_PATH,
  CLIPPING_BILLING: CLIPPING_BILLING_PATH,
  AUTOMATED_DOCUMENT_BILLING: AUTOMATED_DOCUMENT_BILLING_PATH,
  PHONE_AGENT_BILLING: PHONE_AGENT_BILLING_PATH,

  /* --- CONFIGURATOR --- */
  CONFIGURATOR_MODELS: CONFIGURATOR_MODELS_PATH,

  CONFIGURATOR_OFFERS: CONFIGURATOR_OFFERS_PATH,
  CONFIGURATOR_OFFERS_DETAILED: CONFIGURATOR_OFFERS_DETAILED_PATH,

  /* --- KEYDEPOSIT (lockers / appointments) --- */
  KEY_DROP_OFF_LOCKER_PLANNING: KEY_DROP_OFF_LOCKER_PLANNING_PATH,
  KEY_DROP_OFF_LOCKER_DETAILED: KEY_DROP_OFF_LOCKER_DETAILED_PATH,

  KEY_DROP_OFF_APPOINTMENTS: KEY_DROP_OFF_APPOINTMENTS_PATH,
  KEY_DROP_OFF_APPOINTMENTS_DETAILED: KEY_DROP_OFF_APPOINTMENTS_DETAILED_PATH,

  /* --- CHAT / NOTIFICATIONS --- */
  CHAT: CHAT_DASHBOARD_PATH,
  USER_NOTIFICATIONS: USER_NOTIFICATIONS_PATH,
  USER_NOTIFICATIONS_SETTINGS: USER_NOTIFICATIONS_SETTINGS_PATH,

  /* --- DEALER ADMINISTRATION / CONFIGURATION PAGES --- */
  DEALERS: ADMINISTRATION_PATH,
  DEALERS_DETAILS: DEALERS_DETAILS_PATH,

  DEALER_CONFIG_PAGE: DEALER_CONFIG_PAGE_PATH,
  GROUP_CONFIG_PAGE: GROUP_CONFIG_PAGE_PATH,

  /* === DEALER MANAGEMENT === */
  DEALER_MANAGEMENT: DEALER_MANAGEMENT_PATH,
  USER_GROUPS: DEALER_USER_GROUPS_PATH,
  DEVICES: DEALER_DEVICES_PATH,
  WHITELIST: DEALER_WHITELIST_PATH,
  HARDWARES: DEALER_HARDWARES_PATH,
  SAFE_CONFIG: DEALER_SAFES_PATH,
  MEDIA_LIBRARY_DEALER: DEALER_MEDIA_LIBRARY_PATH,
  DEALER_PUBLIC_TOKENS_DASHBOARD: DEALER_PUBLIC_TOKENS_DASHBOARD_PATH,
  DEALER_SMS_ACCOUNTS_DASHBOARD: DEALER_SMS_ACCOUNTS_DASHBOARD_PATH,

  DEALER_CCM: DEALER_CCM_PATH,
  USERS: DEALER_USERS_PATH,
  USERS_DETAILED: DEALER_USERS_DETAILED_PATH,

  PAYERS: DEALER_PAYERS_PATH,
  PAYERS_DETAILS: DEALER_PAYERS_DETAILS_PATH,

  KIOSKS: DEALER_KIOSKS_PATH,
  KIOSKS_DETAILS: DEALER_KIOSKS_DETAILS_PATH,

  CAMERAS: DEALER_CAMERAS_PATH,
  CAMERAS_DETAILS: DEALER_CAMERAS_DETAILS_PATH,

  AUDIT_MODELS: DEALER_AUDIT_MODELS_PATH,
  AUDIT_MODELS_DETAILED: DEALER_AUDIT_MODELS_DETAILED_PATH,

  CHECKLISTS: DEALER_CHECKLISTS_PATH,
  CHECKLISTS_DETAILED: DEALER_CHECKLISTS_DETAILED_PATH,

  GUIDELINES_DASHBOARD: DEALER_GUIDELINES_PATH,
  GUIDELINE_DETAILED: DEALER_GUIDELINE_DETAILED_PATH,

  PREDEFINED_MESSAGES_DASHBOARD: DEALER_PREDEFINED_MESSAGES_PATH,
  PREDEFINED_MESSAGES_DETAILED: DEALER_PREDEFINED_MESSAGES_DETAILED_PATH,

  OFFERS_DASHBOARD: DEALER_OFFERS_PATH,
  OFFERS_DETAILED: DEALER_OFFERS_DETAILED_PATH,

  DEALER_PARTS_OFFERS: DEALER_PARTS_OFFERS_PATH,
  PARTS_OFFERS_DETAILS: DEALER_PARTS_OFFERS_DETAILS_PATH,

  STEPS_DASHBOARD: DEALER_STEPS_DASHBOARD_PATH,
  STEPS_DETAILS: DEALER_STEPS_DETAILS_PATH,

  DEALER_TAGS_DASHBOARD: DEALER_TAGS_DASHBOARD_PATH,
  DEALER_TAGS_DETAILS: DEALER_TAGS_DETAILS_PATH,

  GATE_MANAGEMENT_DASHBOARD: DEALER_GATE_MANAGEMENT_DASHBOARD_PATH,
  GATE_MANAGEMENT_DETAILS: DEALER_GATE_MANAGEMENT_DETAILS_PATH,

  DEALER_CONFIG_SCREENSAVERS: DEALER_CONFIG_SCREENSAVERS_PATH,
  DEALER_CONFIG_SCREENSAVER_DETAILS: DEALER_CONFIG_SCREENSAVER_DETAILS_PATH,

  DEALER_EXTRA_DASHBOARD: DEALER_EXTRA_DASHBOARD_PATH,
  DEALER_EXTRA_DETAILED: DEALER_EXTRA_DETAILED_PATH,

  DEALER_QUESTION_MODEL_DASHBOARD: DEALER_QUESTION_MODEL_DASHBOARD_PATH,
  QUESTION_MODEL_DETAILED: DEALER_QUESTION_MODEL_DETAILED_PATH,

  DEALER_VEHICLE_CHECK_MODEL_DASHBOARD: DEALER_VEHICLE_CHECK_MODEL_DASHBOARD_PATH,
  VEHICLE_CHECK_MODEL_DETAILED: DEALER_VEHICLE_CHECK_MODEL_DETAILED_PATH,

  /* === GROUP MANAGEMENT === */
  GROUP_MANAGEMENT: GROUP_MANAGEMENT_PATH,
  GROUP_TEMPLATES_CONFIG: GROUP_TEMPLATES_PATH,
  MEDIA_LIBRARY_GROUP: GROUP_MEDIA_LIBRARY_PATH,
  GROUP_DEVICES: GROUP_DEVICES_PATH,
  GROUP_PUBLIC_TOKENS_DASHBOARD: GROUP_PUBLIC_TOKENS_DASHBOARD_PATH,
  GROUP_SMS_ACCOUNTS_DASHBOARD: GROUP_SMS_ACCOUNTS_DASHBOARD_PATH,

  GROUP_CCM: GROUP_CCM_PATH,
  GROUP_CHECKLISTS_CONFIG: GROUP_CHECKLISTS_PATH,
  GROUP_CHECKLISTS_DETAILED: GROUP_CHECKLISTS_DETAILED_PATH,

  GROUP_GUIDELINES_CONFIG: GROUP_GUIDELINES_PATH,
  GROUP_GUIDELINE_DETAILED: GROUP_GUIDELINE_DETAILED_PATH,

  GROUP_OFFERS: GROUP_OFFERS_PATH,
  GROUP_OFFERS_DETAILED: GROUP_OFFERS_DETAILED_PATH,

  GROUP_PARTS_OFFERS: GROUP_PARTS_OFFERS_PATH,
  GROUP_PARTS_OFFERS_DETAILS: GROUP_PARTS_OFFERS_DETAILS_PATH,

  GROUP_EXTRA_DASHBOARD: GROUP_EXTRA_DASHBOARD_PATH,
  GROUP_EXTRA_DETAILED: GROUP_EXTRA_DETAILED_PATH,

  GROUP_QUESTION_MODEL_DASHBOARD: GROUP_QUESTION_MODEL_DASHBOARD_PATH,
  GROUP_QUESTION_MODEL_DETAILED: GROUP_QUESTION_MODEL_DETAILED_PATH,

  GROUP_VEHICLE_CHECK_MODEL_DASHBOARD: GROUP_VEHICLE_CHECK_MODEL_DASHBOARD_PATH,
  GROUP_VEHICLE_CHECK_MODEL_DETAILED: GROUP_VEHICLE_CHECK_MODEL_DETAILED_PATH,

  GROUP_PAYERS: GROUP_PAYERS_PATH,
  GROUP_PAYERS_DETAILS: GROUP_PAYERS_DETAILS_PATH,

  GROUP_TAGS_DASHBOARD: GROUP_TAGS_DASHBOARD_PATH,
  GROUP_TAGS_DETAILS: GROUP_TAGS_DETAILS_PATH,

  GROUP_CONFIG_SCREENSAVERS: GROUP_CONFIG_SCREENSAVERS_PATH,
  GROUP_CONFIG_SCREENSAVER_DETAILS: GROUP_CONFIG_SCREENSAVER_DETAILS_PATH,

  /* === PERSONAL MANAGEMENT === */
  PERSONAL_CONFIG: PERSONAL_MANAGEMENT_PATH,
  MEDIA_LIBRARY_PERSONAL: PERSONAL_MEDIA_LIBRARY_PATH
};

export const ROUTES = {
  HOME: 'HOME',
  PLAYGROUND: 'PLAYGROUND',
  ERROR_404: 'ERROR_404',
  ERROR_GLOBAL: 'ERROR_GLOBAL',
  MAGIC_LINK: 'MAGIC_LINK',

  KIOSKS: 'KIOSKS',
  KIOSKS_DETAILS: 'KIOSKS_DETAILS',

  CAMERAS: 'CAMERAS',
  CAMERAS_DETAILS: 'CAMERAS_DETAILS',

  WHITELIST: 'WHITELIST',

  HARDWARES: 'HARDWARES',

  PAYMENT: 'PAYMENT',
  AFTERSALES: 'AFTERSALES',
  BODYWORKS: 'BODYWORKS',
  REMINDERS: 'REMINDERS',
  SELF_SERVICE_KANBAN: 'SELF_SERVICE_KANBAN',
  SELF_SERVICE_MOBILITY: 'SELF_SERVICE_MOBILITY',

  FOLLOW_UP: 'FOLLOW_UP',

  SALES: 'SALES',
  LEADS: 'LEADS',
  OFFERS_DASHBOARD: 'OFFERS_DASHBOARD',

  PARTS: 'PARTS',
  PARTS_DETAILED: 'PARTS_DETAILED',
  PARTS_REMINDER: 'PARTS_REMINDER',
  PARTS_REMINDER_DETAILED: 'PARTS_REMINDER_DETAILED',

  AUDITS: 'AUDITS',
  AUDITS_DETAILED: 'AUDITS_DETAILED',

  CUSTOMERS: 'CUSTOMERS',

  STATS_PARTS: 'STATS_PARTS',
  STATS_SALES: 'STATS_SALES',
  STATS_AUDITS: 'STATS_AUDITS',
  STATS_AFTER_SALES: 'STATS_AFTER_SALES',
  STATS_PARTS_DETAILS: 'STATS_PARTS_DETAILS',
  STATS_SALES_DETAILS: 'STATS_SALES_DETAILS',
  MANUFACTURER_STATS_PARTS: 'MANUFACTURER_STATS_PARTS',
  MANUFACTURER_STATS_SALES: 'MANUFACTURER_STATS_SALES',
  STATS_AFTER_SALES_DETAILS: 'STATS_AFTER_SALES_DETAILS',
  SELF_SERVICE_STATS_DETAILS: 'SELF_SERVICE_STATS_DETAILS',
  SELF_SERVICE_STATS_DASHBOARD: 'SELF_SERVICE_STATS_DASHBOARD',
  MANUFACTURER_STATS_AFTER_SALES: 'MANUFACTURER_STATS_AFTER_SALES',
  MANUFACTURER_STATS_SELF_SERVICE: 'MANUFACTURER_STATS_SELF_SERVICE',
  MANUFACTURER_STATS_PARTS_DETAILS: 'MANUFACTURER_STATS_PARTS_DETAILS',
  MANUFACTURER_STATS_SALES_DETAILS: 'MANUFACTURER_STATS_SALES_DETAILS',
  MANUFACTURER_STATS_AFTER_SALES_DETAILS: 'MANUFACTURER_STATS_AFTER_SALES_DETAILS',
  MANUFACTURER_STATS_SELF_SERVICE_DETAILS: 'MANUFACTURER_STATS_SELF_SERVICE_DETAILS',

  CONFIGURATOR_OFFERS: 'CONFIGURATOR_OFFERS',
  CONFIGURATOR_MODELS: 'CONFIGURATOR_MODELS',

  KEY_DROP_OFF_LOCKER_DETAILED: 'KEY_DROP_OFF_LOCKER_DETAILED',
  KEY_DROP_OFF_APPOINTMENTS: 'KEY_DROP_OFF_APPOINTMENTS',
  KEY_DROP_OFF_LOCKER_PLANNING: 'KEY_DROP_OFF_LOCKER_PLANNING',

  GROUP_CONFIGURATION: 'GROUP_CONFIGURATION',
  DEALER_CONFIGURATION: 'DEALER_CONFIGURATION',
  PERSONAL_CONFIGURATION: 'PERSONAL_CONFIGURATION',

  DEALER_MANAGEMENT: 'DEALER_MANAGEMENT',
  USERS: 'USERS',
  PAYERS: 'PAYERS',
  PAYERS_DETAILS: 'PAYERS_DETAILS',
  USER_CONFIG: 'USER_CONFIG',
  DEVICES: 'DEVICES',
  GROUP_DEVICES: 'GROUP_DEVICES',
  CHECKLISTS: 'CHECKLISTS',
  USER_GROUPS: 'USER_GROUPS',
  SAFE_CONFIG: 'SAFE_CONFIG',
  AUDIT_MODELS: 'AUDIT_MODELS',
  STEPS_DETAILS: 'STEPS_DETAILS',
  STEPS_DASHBOARD: 'STEPS_DASHBOARD',
  GUIDELINE_DETAILED: 'GUIDELINE_DETAILED',
  GUIDELINES_DASHBOARD: 'GUIDELINES_DASHBOARD',
  PREDEFINED_MESSAGES_DETAILED: 'PREDEFINED_MESSAGES_DETAILED',
  PREDEFINED_MESSAGES_DASHBOARD: 'PREDEFINED_MESSAGES_DASHBOARD',
  DEALER_EXTRA_DASHBOARD: 'DEALER_EXTRA_DASHBOARD',
  DEALER_QUESTION_MODEL_DASHBOARD: 'DEALER_QUESTION_MODEL_DASHBOARD',
  DEALER_VEHICLE_CHECK_MODEL_DASHBOARD: 'DEALER_VEHICLE_CHECK_MODEL_DASHBOARD',
  GROUP_EXTRA_DASHBOARD: 'GROUP_EXTRA_DASHBOARD',
  GROUP_QUESTION_MODEL_DASHBOARD: 'GROUP_QUESTION_MODEL_DASHBOARD',
  GROUP_VEHICLE_CHECK_MODEL_DASHBOARD: 'GROUP_VEHICLE_CHECK_MODEL_DASHBOARD',
  DEALER_EXTRA_DETAILED: 'DEALER_EXTRA_DETAILED',
  GROUP_EXTRA_DETAILED: 'GROUP_EXTRA_DETAILED',
  GROUP_QUESTION_MODEL_DETAILED: 'GROUP_QUESTION_MODEL_DETAILED',
  GROUP_VEHICLE_CHECK_MODEL_DETAILED: 'GROUP_VEHICLE_CHECK_MODEL_DETAILED',
  QUESTION_MODEL_DETAILED: 'QUESTION_MODEL_DETAILED',
  VEHICLE_CHECK_MODEL_DETAILED: 'VEHICLE_CHECK_MODEL_DETAILED',

  MEDIA_LIBRARY_DEALER: 'MEDIA_LIBRARY_DEALER',
  MEDIA_LIBRARY_GROUP: 'MEDIA_LIBRARY_GROUP',
  MEDIA_LIBRARY_PERSONAL: 'MEDIA_LIBRARY_PERSONAL',

  BILLING: 'BILLING',
  MESSAGE_BILLING: 'MESSAGE_BILLING',
  CLIPPING_BILLING: 'CLIPPING_BILLING',
  AUTOMATED_DOCUMENT_BILLING: 'AUTOMATED_DOCUMENT_BILLING',
  PHONE_AGENT_BILLING: 'PHONE_AGENT_BILLING',

  CHAT: 'CHAT',

  DEALERS: 'DEALERS',

  PROFILE: 'PROFILE',

  GROUP_CONFIG_PAGE: 'GROUP_CONFIG_PAGE',

  GROUP_OFFERS: 'GROUP_OFFERS',
  GROUP_TEMPLATES: 'GROUP_TEMPLATES',
  GROUP_GUIDELINES: 'GROUP_GUIDELINES',
  GROUP_GUIDELINE_DETAILED: 'GROUP_GUIDELINE_DETAILED',
  GROUP_CHECKLISTS: 'GROUP_CHECKLISTS',
  GROUP_PAYERS: 'GROUP_PAYERS',
  GROUP_PAYERS_DETAILS: 'GROUP_PAYERS_DETAILS',

  USER_NOTIFICATIONS: 'USER_NOTIFICATIONS',
  USER_NOTIFICATIONS_SETTINGS: 'USER_NOTIFICATIONS_SETTINGS',

  // Parts offers
  GROUP_PARTS_OFFERS: 'GROUP_PARTS_OFFERS',
  GROUP_PARTS_OFFERS_DETAILS: 'GROUP_PARTS_OFFERS_DETAILS',
  GROUP_CHECKLISTS_DETAILED: 'GROUP_CHECKLISTS_DETAILED',
  GROUP_OFFERS_DETAILED: 'GROUP_OFFERS_DETAILED',
  DEALER_PARTS_OFFERS: 'DEALER_PARTS_OFFERS',
  PARTS_OFFERS_DETAILS: 'PARTS_OFFERS_DETAILS',

  DEALER_CONFIG_PAGE: 'DEALER_CONFIG_PAGE',

  CAMPAIGNS_DASHBOARD: 'CAMPAIGNS_DASHBOARD',
  CAMPAIGNS_DETAILS: 'CAMPAIGNS_DETAILS',
  CONTACT_LISTS_DASHBOARD: 'CONTACT_LISTS_DASHBOARD',
  CONTACT_LISTS_DETAILS: 'CONTACT_LISTS_DETAILS',
  RELEASES_DASHBOARD: 'RELEASES_DASHBOARD',
  RELEASES_DETAILS: 'RELEASES_DETAILS',

  // Screensavers
  GROUP_CONFIG_SCREENSAVERS: 'GROUP_CONFIG_SCREENSAVERS',
  DEALER_CONFIG_SCREENSAVERS: 'DEALER_CONFIG_SCREENSAVERS',
  GROUP_CONFIG_SCREENSAVER_DETAILS: 'GROUP_CONFIG_SCREENSAVER_DETAILS',
  DEALER_CONFIG_SCREENSAVER_DETAILS: 'DEALER_CONFIG_SCREENSAVER_DETAILS',

  // Tags management
  GROUP_TAGS_DASHBOARD: 'GROUP_TAGS_DASHBOARD',
  GROUP_TAGS_DETAILS: 'GROUP_TAGS_DETAILS',
  DEALER_TAGS_DETAILS: 'DEALER_TAGS_DETAILS',
  DEALER_TAGS_DASHBOARD: 'DEALER_TAGS_DASHBOARD',

  // Public Tokens
  GROUP_PUBLIC_TOKENS_DASHBOARD: 'GROUP_PUBLIC_TOKENS_DASHBOARD',
  DEALER_PUBLIC_TOKENS_DASHBOARD: 'DEALER_PUBLIC_TOKENS_DASHBOARD',

  // SMS accounts
  GROUP_SMS_ACCOUNTS_DASHBOARD: 'GROUP_SMS_ACCOUNTS_DASHBOARD',
  DEALER_SMS_ACCOUNTS_DASHBOARD: 'DEALER_SMS_ACCOUNTS_DASHBOARD',

  // CCM
  DEALER_CCM: 'DEALER_CCM',
  GROUP_CCM: 'GROUP_CCM',

  // Gate Management
  GATE_MANAGEMENT_DASHBOARD: 'GATE_MANAGEMENT_DASHBOARD',
  GATE_MANAGEMENT_DETAILS: 'GATE_MANAGEMENT_DETAILS'
};

export const DEALER_CONFIG_PAGES = {
  GLOBAL: 'UI_DEALER_CONFIG_GLOBAL',
  CHAT: 'UI_DEALER_CONFIG_CHAT',
  PARTS: 'UI_DEALER_CONFIG_PARTS',
  PARTS_STEPS: 'UI_DEALER_CONFIG_PARTS_STEPS',
  PARTS_CLIPPING: 'UI_DEALER_CONFIG_PARTS_CLIPPING',
  ADMINISTRATION: 'UI_DEALER_CONFIG_ADMINISTRATION',
  NOTIFICATION_AFTERSALES: 'UI_DEALER_CONFIG_NOTIFICATION_AFTERSALES',
  NOTIFICATION_SELFSERVICE: 'UI_DEALER_CONFIG_NOTIFICATION_SELFSERVICE',
  NOTIFICATION_PARTS: 'UI_DEALER_CONFIG_NOTIFICATION_PARTS',
  NOTIFICATION_SALES: 'UI_DEALER_CONFIG_NOTIFICATION_SALES',
  NOTIFICATION_CHATBOT: 'UI_DEALER_CONFIG_NOTIFICATION_CHATBOT',
  NOTIFICATION_ONLINEPAYMENT: 'UI_DEALER_CONFIG_NOTIFICATION_ONLINEPAYMENT',
  NOTIFICATION_AUDIT: 'UI_DEALER_CONFIG_NOTIFICATION_AUDIT',
  NOTIFICATION_KEYDEPOSIT: 'UI_DEALER_CONFIG_NOTIFICATION_KEYDEPOSIT',
  NOTIFICATION_CHAT: 'UI_DEALER_CONFIG_NOTIFICATION_CHAT',
  AUDIT: 'UI_DEALER_CONFIG_AUDIT',
  AFTERSALES: 'UI_DEALER_CONFIG_AFTERSALES',
  SALES: 'UI_DEALER_CONFIG_SALES',
  SALES_CLIPPING: 'UI_DEALER_CONFIG_SALES_CLIPPING',
  KEYDEPOSIT: 'UI_DEALER_CONFIG_KEYDEPOSIT',
  CUSTOMISATION_BRANDING: 'UI_DEALER_CONFIG_CUSTOMISATION_BRANDING',
  CUSTOMISATION_TEMPLATES: 'UI_DEALER_CONFIG_CUSTOMISATION_TEMPLATES',
  SELFSERVICE: 'UI_DEALER_CONFIG_SELFSERVICE',
  STATISTICS: 'UI_DEALER_CONFIG_STATISTICS',
  INTEGRATION: 'UI_DEALER_CONFIG_INTEGRATION',
  INTEGRATION_ACTIVATION: 'UI_DEALER_CONFIG_INTEGRATION_ACTIVATION',
  INTEGRATION_SMS_PROVIDER: 'UI_DEALER_CONFIG_INTEGRATION_SMSPROVIDER'
};

export const GROUP_CONFIG_PAGES = {
  SALES: 'UI_GROUP_CONFIG_SALES',
  AFTERSALES: 'UI_GROUP_CONFIG_AFTERSALES',
  PARTS: 'UI_GROUP_CONFIG_PARTS',
  SELFSERVICE: 'UI_GROUP_CONFIG_SELFSERVICE',
  // Tags
  SALES_TAGS: 'UI_GROUP_CONFIG_SALES_TAGS',
  AFTERSALES_TAGS: 'UI_GROUP_CONFIG_AFTERSALES_TAGS',
  PARTS_TAGS: 'UI_GROUP_CONFIG_PARTS_TAGS'
};

export const HOME_PAGES = [
  PATH.AFTERSALES,
  PATH.SALES,
  PATH.PARTS,
  PATH.AUDITS,
  PATH.STATS_AFTER_SALES,
  PATH.STATS_PARTS,
  PATH.STATS_SALES,
  PATH.SELF_SERVICE_STATS_DASHBOARD,
  PATH.MANUFACTURER_STATS_AFTER_SALES,
  PATH.MANUFACTURER_STATS_SELF_SERVICE,
  PATH.MANUFACTURER_STATS_PARTS,
  PATH.MANUFACTURER_STATS_SALES,
  PATH.STATS_ODITOO,
  PATH.KEY_DROP_OFF_LOCKER_PLANNING,
  PATH.CUSTOMERS,
  PATH.CAMPAIGNS_DASHBOARD,
  PATH.ERROR_404
];

export const INTERVENTION_PATHS_BY_TYPE = {
  [ENTITY_TYPE.PARTS_INTERVENTION]: [PATH.PARTS_DETAILED, PATH.PARTS_REMINDER_DETAILED],

  [ENTITY_TYPE.INTERVENTION]: [PATH.AFTERSALES_DETAILED, PATH.REMINDERS_DETAILED, PATH.SALES_DETAILED, PATH.BODYWORKS_DETAILED]
};
